// 自研IM兼容层（PC站，替代腾讯云IM SDK）
// 保留原 TIM SDK 方法签名，内部调用自研后端 API
import Vue from "vue";
import Event from "./event";
import store from "@/store/index";
import service from "@/api/request";
let eventInstance = new Event();

function request(url, method = "GET") {
  return new Promise((resolve, reject) => {
    service({
      url,
      method,
      timeout: 15000,
    }).then((res) => {
      if (res && res.data && res.data.code === 200) {
        resolve(res.data.data);
      } else {
        reject(res.data || { message: "request error" });
      }
    }).catch(reject);
  });
}

// ---- 常量（兼容 $TencentCloudChatType / $TencentCloudChat）----
const TYPES = {
  CONV_C2C: "C2C",
  CONV_GROUP: "GROUP",
  MSG_TEXT: "TIMTextElem",
  MSG_IMAGE: "TIMImageElem",
  MSG_AUDIO: "TIMSoundElem",
  MSG_VIDEO: "TIMVideoFileElem",
  MSG_FILE: "TIMFileElem",
  MSG_CUSTOM: "TIMCustomElem",
};

const EVENT = {
  SDK_READY: "TencentCloudChat.EVENT.SDK_READY",
  SDK_NOT_READY: "TencentCloudChat.EVENT.SDK_NOT_READY",
  MESSAGE_RECEIVED: "TencentCloudChat.EVENT.MESSAGE_RECEIVED",
  MESSAGE_REVOKED: "TencentCloudChat.EVENT.MESSAGE_REVOKED",
  MESSAGE_READ_BY_PEER: "TencentCloudChat.EVENT.MESSAGE_READ_BY_PEER",
  KICKED_OUT: "TencentCloudChat.EVENT.KICKED_OUT",
  NET_STATE_CHANGE: "TencentCloudChat.EVENT.NET_STATE_CHANGE",
  CONVERSATION_LIST_UPDATED: "TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED",
  TOTAL_UNREAD_MESSAGE_COUNT_UPDATED: "TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED",
  LOGIN_SUCCESS: "TencentCloudChat.EVENT.LOGIN_SUCCESS",
};

let _userId = "";
let _pollTimer = null;

// 后端消息 -> TIM 消息结构
function toTimMessage(m) {
  const isMine = m.fromId === _userId;
  const peer = isMine ? m.toId : m.fromId;
  // gp-* 客服/系统对端输出 C2Cgp-service 格式（与页面精确比较一致）；普通用户输出 C2C_<对方>_<我>
  const convId = /^gp-/.test(peer || "") ? "C2C" + peer : "C2C_" + (isMine ? m.toId + "_" + m.fromId : m.fromId + "_" + m.toId);
  const type = m.msgType === "CUSTOM" ? TYPES.MSG_CUSTOM : TYPES.MSG_TEXT;
  return {
    ID: m.id,
    conversationID: convId,
    from: m.fromId,
    to: m.toId,
    type,
    time: Math.floor(new Date(m.createDate).getTime() / 1000),
    payload: type === TYPES.MSG_TEXT ? { text: m.content } : { data: m.content, description: m.msgDesc },
    nick: m.fromId,
    messageForShow: m.content,
    isRead: m.isRead,
  };
}

// 后端会话 -> TIM conversation 结构
function toTimConversation(c) {
  // gp-* 客服/系统对端输出 C2Cgp-service 格式（与页面精确比较一致）；普通用户输出 C2C_<userId>_<peerId>
  const convId = /^gp-/.test(c.peerId || "") ? "C2C" + c.peerId : "C2C_" + c.userId + "_" + c.peerId;
  const lastTime = c.lastMessageTime ? Math.floor(new Date(c.lastMessageTime).getTime() / 1000) : Math.floor(Date.now() / 1000);
  return {
    conversationID: convId,
    type: TYPES.CONV_C2C,
    unreadCount: c.unreadCount || 0,
    lastMessage: {
      lastTime,
      messageForShow: c.lastMessage || "",
      nick: c.peerName || c.peerId,
      fromAccount: c.peerId,
    },
    groupProfile: null,
  };
}

// ---- 兼容 TIM 的 chat 实例 ----
const chat = {
  setLogLevel() {},
  registerPlugin() {},
  login({ userID }) {
    _userId = userID;
    $SKD_STATUS.$SKD_READY = true;
    $SKD_STATUS.$LOGIN_SUCCESS = true;
    eventInstance.emit("TencentCloudChat.EVENT.SDK_READY", {});
    eventInstance.emit("TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED", { data: 0 });
    startPolling();
    return Promise.resolve({ data: { repeatLogin: false } });
  },
  logout() {
    _userId = "";
    stopPolling();
    return Promise.resolve({ data: {} });
  },
  on(event, cb) {},
  off(event, cb) {},
  getConversationList() {
    return new Promise((resolve, reject) => {
      request(`/api/im/conversations?userId=${_userId}`)
        .then((list) => {
          const conversationList = (list || []).map(toTimConversation);
          resolve({ data: { conversationList, isSyncCompleted: true } });
        })
        .catch(reject);
    });
  },
  getConversationProfile(conversationID) {
    return new Promise((resolve, reject) => {
      request(`/api/im/conversations?userId=${_userId}`)
        .then((list) => {
          const parts = (conversationID || "").split("_");
          // 与 getMessageList 同款解析：正向/反向 C2C_<a>_<b> 均取对端；C2C<peer> 客服会话去前缀
          const peerId = parts.length >= 3 ? (parts[1] === _userId ? parts[2] : parts[1]) : (conversationID || "").replace(/^C2C/, "");
          const conv = (list || []).find((c) => c.peerId === peerId);
          // code: 0 与页面调用方判断（imResponse.code == 0）对齐，否则未读角标永不更新
          resolve({ code: 0, data: { conversation: conv ? toTimConversation(conv) : null } });
        })
        .catch(reject);
    });
  },
  getMessageList({ conversationID }) {
    return new Promise((resolve, reject) => {
      const parts = (conversationID || "").split("_");
      // C2C_<a>_<b> 取对端；C2C<peer>（如 C2Cgp-service 客服/系统会话）去掉 C2C 前缀即对端
      const peerId = parts.length >= 3 ? (parts[1] === _userId ? parts[2] : parts[1]) : (conversationID || "").replace(/^C2C/, "");
      const conv = `${_userId}_${peerId}`;
      request(`/api/im/messages?conversationId=${conv}&limit=50`)
        .then((list) => {
          const messageList = (list || []).map(toTimMessage);
          resolve({ data: { messageList, nextReqMessageID: "", isCompleted: true } });
        })
        .catch(reject);
    });
  },
  sendMessage(message) {
    const cid = message.conversationID || "";
    const parts = cid.split("_");
    const toId = message.to || (parts.length >= 3 ? (parts[1] === _userId ? parts[2] : parts[1]) : cid.replace(/^C2C/, ""));
    const payload = message.payload || {};
    let content = payload.text || "";
    if (!content && payload.data) {
      content = typeof payload.data === "string" ? payload.data : JSON.stringify(payload.data);
    }
    return new Promise((resolve, reject) => {
      request(`/api/im/send?fromId=${_userId}&toId=${toId}&msg=${encodeURIComponent(content)}`, "POST")
        .then(() => {
          eventInstance.emit("TencentCloudChat.EVENT.MESSAGE_RECEIVED", { data: [] });
          resolve({ data: {} });
        })
        .catch(reject);
    });
  },
  setMessageRead({ conversationID }) {
    const parts = (conversationID || "").split("_");
    // C2C_<a>_<b> 取对端；C2C<peer>（如 C2Cgp-service 客服/系统会话）去掉 C2C 前缀即对端
    const peerId = parts.length >= 3 ? (parts[1] === _userId ? parts[2] : parts[1]) : (conversationID || "").replace(/^C2C/, "");
    const conv = `${_userId}_${peerId}`;
    return new Promise((resolve, reject) => {
      request(`/api/im/read?userId=${_userId}&conversationId=${conv}`, "POST")
        .then(() => resolve({ data: {} }))
        .catch(reject);
    });
  },
  getTotalUnreadMessageCount() {
    return store.state.unreadCount || 0;
  },
  // 消息构造（兼容 createTextMessage 等）
  createTextMessage(message) {
    return { ...message, type: TYPES.MSG_TEXT, payload: { text: message.payload.text } };
  },
  createImageMessage(message) {
    return { ...message, type: TYPES.MSG_IMAGE, payload: message.payload };
  },
  createVideoMessage(message) {
    return { ...message, type: TYPES.MSG_VIDEO, payload: message.payload };
  },
};

// ---- 轮询未读数 ----
function startPolling() {
  stopPolling();
  _pollTimer = setInterval(() => {
    request(`/api/im/unread?userId=${_userId}`)
      .then((data) => {
        const count = (data && data.unread) || 0;
        store.dispatch("getIMMsgUnreadCount", count);
        eventInstance.emit("TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED", { data: count });
        eventInstance.emit("TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED", {});
      })
      .catch(() => {});
  }, 5000);
}

function stopPolling() {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
}

// ---- 全局变量（兼容原 create.js 导出）----
let $TencentCloudChat = { TYPES, EVENT };
let $TencentCloudChatEvent = EVENT;
let $TencentCloudChatType = TYPES;
let $SKD_STATUS = {
  $SKD_READY: false,
  $LOGIN_SUCCESS: false,
};

Vue.prototype.$TUIKIT = chat;

export {
  chat,
  $TencentCloudChat,
  $TencentCloudChatEvent,
  $TencentCloudChatType,
  $SKD_STATUS,
  eventInstance
};
